import { useMemo } from 'react';
import { useAuth } from '@/features/auth/authContext';
import { useNavPreferences } from '@/navigation/navPreferences';
import { railLabelFor } from '@/navigation/hubs';
import { applySidebarLayout, EMPTY_SIDEBAR_LAYOUT } from './sidebarLayout';
import {
  NAV_GROUPS,
  filterNavGroupsForPermissions,
} from '@/navigation/registry';
import type { NavDomain } from '@/navigation/types';
import { useNavPreferenceScope, useScopedNavPreferences } from './useScopedNavPreferences';

/** RBAC-filtered tree minus pages the operator hid from the rail. */
export function useSidebarNavGroups(): NavDomain[] {
  const permissions = useAuth().permissions;
  const scope = useSidebarLayoutScope();
  const { hiddenIds } = useScopedNavPreferences();
  const layout = useNavPreferences((state) => scope ? state.sidebarLayouts[scope] : undefined);

  return useMemo(
    () => {
      const labeled = filterNavGroupsForPermissions(NAV_GROUPS, permissions).map((group) => ({
        ...group,
        items: group.items.map((item) => ({ ...item, label: railLabelFor(item.id, item.label) })),
      }));
      const hidden = new Set(hiddenIds ?? []);
      return applySidebarLayout(labeled, layout ?? EMPTY_SIDEBAR_LAYOUT)
        .flatMap((group) => {
          const items = group.items.filter((item) => !hidden.has(item.id));
          // Keep move-emptied drop targets, while honoring a choice to hide every page.
          return group.items.length === 0 || items.length > 0 ? [{ ...group, items }] : [];
        });
    },
    [hiddenIds, layout, permissions],
  );
}

/** No anonymous/shared fallback: personal layouts require an authenticated identity. */
export function useSidebarLayoutScope(): string | null {
  return useNavPreferenceScope();
}

/** Pages the signed-in session may reach — used by the hide/show Settings list. */
export function useHideableNavGroups(): NavDomain[] {
  const permissions = useAuth().permissions;
  const scope = useSidebarLayoutScope();
  const layout = useNavPreferences((state) => scope ? state.sidebarLayouts[scope] : undefined);
  return useMemo(
    () => applySidebarLayout(filterNavGroupsForPermissions(NAV_GROUPS, permissions).map((group) => ({ ...group, items: group.items.map((item) => ({ ...item, label: railLabelFor(item.id, item.label) })) })), layout ?? EMPTY_SIDEBAR_LAYOUT),
    [layout, permissions],
  );
}
